<template>
  <div>
    <confirm ref="confirmStorno" @selection="cancelReceipt" />
    <confirm ref="confirmFiscalize" @selection="fiscalizeReceipt" />
    <v-container>
      <v-row class="justify-center mt-4">
        <v-col cols="12" sm="11" lg="11" class="px-0">
          <div class="table-header-filters">
            <v-select
              label="Status računa"
              v-model="statusFilter"
              :items="filters"
              return-object
              item-text="name"
              item-value="value"
              dense outlined hide-details
              class="filter-select"
              style="max-width: 220px;"
            >
              <template v-slot:item="{ item }">{{ item.name }}</template>
            </v-select>
          </div>
        </v-col>
      </v-row>
      <v-row class="justify-center">
        <v-col cols="12" sm="11" lg="11" class="px-0">
          <div class="receipts-rows-body">
          <v-expansion-panels focusable>
            <v-expansion-panel
              v-for="receipt in receipts"
              :key="receipt.id"
              hover
              tabindex="0"
              :class="[billingStatusClass(receipt)]"
            >
              <v-expansion-panel-header height="200" hide-actions>
                <v-row align="center">
                  <v-col cols="auto" :class="[densityPadding]" style="min-width: 130px;">
                    <div class="caption grey--text text-left">Račun</div>
                    <div class="text-left receipt-number-value">{{ receipt.receiptNumber }}</div>
                  </v-col>
                  <v-col :class="[densityPadding]" style="min-width: 0; flex-grow: 1; max-width: none;"></v-col>
                  <v-col cols="auto" :class="[densityPadding]" style="min-width: 110px;">
                    <div class="caption grey--text text-right">Iznos</div>
                    <div class="text-right receipt-amount-value">
                      {{ receipt.total | money(100, receipt.currency) }}
                    </div>
                  </v-col>
                  <v-col cols="2" :class="[densityPadding]">
                    <div class="caption grey--text text-center">Datum izdavanja</div>
                    <div class="text-center text-truncate">{{ receipt.issue_date_time | local }}</div>
                  </v-col>
                  <v-col cols="2" :class="[densityPadding]">
                    <div class="caption grey--text text-center">Dospijeće</div>
                    <div class="text-center text-truncate">{{ receipt.payment_due | local }}</div>
                  </v-col>
                  <v-col cols="auto" :class="[densityPadding, 'd-none', 'd-md-block']" style="max-width: 130px; min-width: 130px; overflow: hidden;">
                    <div class="caption grey--text text-right">Status</div>
                    <div class="text-right receipt-status-value">
                      {{ $t(`$vuetify.receipts.${receipt.payment ? receipt.payment.current_status : 'UNKNOWN_STATUS'}`) }}
                    </div>
                  </v-col>
                  <v-col cols="auto" class="d-flex justify-end align-center ml-auto" :class="[densityPadding]" style="z-index: 999; flex-grow: 0; flex-shrink: 0;">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn min-height="30" min-width="25" plain rounded class="download-btn" v-bind="attrs" v-on="on" @click.native.stop @keypress.enter.prevent="printReceipt(receipt)" @click="printReceipt(receipt)">
                          <span class="pdf-icon-stack">
                            <v-icon class="download-icon" color="#D32F2F">mdi-file-pdf-box</v-icon>
                            <v-icon class="download-badge-icon">mdi-arrow-down-circle</v-icon>
                          </span>
                        </v-btn>
                      </template>
                      <span>Preuzmi račun</span>
                    </v-tooltip>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" sm="5">
                    <div
                      v-if="
                        receipt.payment_method === 'TRANSAKCIJSKI RAČUN' &&
                        receipt.payment.current_status !== 'PAID'
                      "
                      class="d-flex justify-space-between text-center text-subtitle-2"
                    >
                      <span style="font-weight: 600;">Rok plaćanja:</span>
                      {{ showPaymentDueDays(receipt.payment_due) }}
                    </div>
                    <div
                      class="d-flex justify-space-between text-center text-subtitle-2"
                    >
                      <span style="font-weight: 600;">Operater:</span> {{ receipt.seller_id }}
                    </div>
                    <div
                      class="d-flex justify-space-between text-center text-subtitle-2"
                    >
                      <span style="font-weight: 600;">Način plaćanja:</span> {{ receipt.payment_method }}
                    </div>
                    <div
                      v-if="receipt.payment_due"
                      class="d-flex justify-space-between text-center text-subtitle-2"
                    >
                      <span style="font-weight: 600;">Dospijeće:</span> {{ formatDateOnly(receipt.payment_due) }}
                    </div>
                  </v-col>
                </v-row>
                <v-list>
                  <v-row dense>
                    <v-col cols="auto" style="width: 48px; flex: 0 0 48px;">
                      <div class="text-center text-subtitle-2 one-liner-text font-weight-bold">
                        R. br.
                      </div>
                    </v-col>
                    <v-col style="min-width: 0; flex-grow: 1; max-width: none;">
                      <div class="text-left text-subtitle-2 one-liner-text font-weight-bold">
                        Naziv
                      </div>
                    </v-col>
                    <v-col cols="auto" style="width: 80px; flex: 0 0 80px;">
                      <div class="text-right text-subtitle-2 one-liner-text font-weight-bold">
                        Količina
                      </div>
                    </v-col>
                    <v-col cols="auto" style="width: 120px; flex: 0 0 120px;">
                      <div class="text-right text-subtitle-2 one-liner-text font-weight-bold">
                        Jedinična cijena
                      </div>
                    </v-col>
                    <v-col cols="auto" style="width: 100px; flex: 0 0 100px;">
                      <div class="text-right text-subtitle-2 one-liner-text font-weight-bold">
                        Cijena
                      </div>
                    </v-col>
                  </v-row>
                  <v-list-item
                    v-for="(item, index) in receipt.items"
                    :key="index"
                    class="pa-0"
                    style="min-height: unset;"
                  >
                    <v-row class="pa-0" dense>
                      <v-col cols="auto" style="width: 48px; flex: 0 0 48px;">
                        <div class="text-center text-subtitle-2">
                          {{ index + 1 }}
                        </div>
                      </v-col>
                      <v-col style="min-width: 0; flex-grow: 1; max-width: none;">
                        <div class="text-left text-subtitle-2">
                          {{ item.name }}
                        </div>
                      </v-col>
                      <v-col cols="auto" style="width: 80px; flex: 0 0 80px;">
                        <div class="text-right text-subtitle-2">
                          {{ formatQty(item.amt) }}
                        </div>
                      </v-col>
                      <v-col cols="auto" style="width: 120px; flex: 0 0 120px;">
                        <div class="text-right text-subtitle-2">
                          {{ item.single_item_price | money(100, receipt.currency) }}
                        </div>
                      </v-col>
                      <v-col cols="auto" style="width: 100px; flex: 0 0 100px;">
                        <div class="text-right text-subtitle-2">
                          {{ item.price | money(100, receipt.currency) }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-list-item>
                </v-list>

                <!-- PDV / PNP breakdown -->
                <div
                  v-if="(receipt.pdv && receipt.pdv.length > 0) || receipt.pnp"
                  class="d-flex justify-end mt-3"
                >
                  <table class="pdv-breakdown-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Stopa</th>
                        <th>Osnovica</th>
                        <th>Iznos</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(pdv, pi) in receipt.pdv" :key="'pdv-' + pi">
                        <td class="pdv-label">PDV</td>
                        <td>{{ pdv.rate / 100 }}%</td>
                        <td>{{ pdv.base | money(100, receipt.currency) }}</td>
                        <td>{{ pdv.total | money(100, receipt.currency) }}</td>
                      </tr>
                      <tr v-if="receipt.pnp">
                        <td class="pdv-label">PNP</td>
                        <td>{{ receipt.pnp.rate / 100 }}%</td>
                        <td>{{ receipt.pnp.base | money(100, receipt.currency) }}</td>
                        <td>{{ receipt.pnp.total | money(100, receipt.currency) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <div v-if="infiniteCompleted && receipts.length === 0 && statusFilter.value === 'unpayed'" class="empty-state">
            <v-icon size="56" color="#4CAF50" class="mb-3">mdi-check-circle-outline</v-icon>
            <div class="empty-state-title">Svi vaši računi su plaćeni</div>
            <div class="empty-state-subtitle">Hvala vam na urednom poslovanju. Zahvaljujemo na povjerenju.</div>
            <v-btn outlined class="empty-state-btn mt-4" @click="statusFilter = { name: 'Svi', value: 'all' }">
              <v-icon left small>mdi-receipt-text-outline</v-icon>
              Vidi sve račune
            </v-btn>
          </div>

          </div>
        </v-col>
      </v-row>
      <infinite-loading
        ref="InfiniteLoading"
        @infinite="infiniteHandler"
        spinner="waveDots"
      >
        <div slot="no-more"></div>
        <div slot="no-results"></div>
      </infinite-loading>
      <generate-receipt-from-offer ref="receiptFromOffer" />
      <send-email ref="ordinaryEmail" @receiptEdit="onReceiptEdit"></send-email>
      <send-email ref="clientWarning" @receiptEdit="onReceiptEdit"></send-email>
      <pay-receipt ref="payReceipt" @receiptEdit="onReceiptEdit" />
      <status-to-unpaid
        ref="statusToUnpaid"
        @receiptEdit="onReceiptEdit"
      ></status-to-unpaid>
    </v-container>
  </div>
</template>
<script>
import { df, auth, functions } from '@/plugins/firebase'
import state from '@/state'
import { v4 as uuidv4 } from 'uuid'
import InfiniteLoading from 'vue-infinite-loading'
import Confirm from '@/components/Confirm.vue'
import flow from '@/mixins/flow'
import applicationSettings from '@/mixins/applicationSettings'
import { clone, toISOLocal } from '@/plugins/utils'
import GenerateReceiptFromOffer from '@/modules/company/components/GenerateReceiptFromOffer'
import PayReceipt from '@/modules/company/components/PayReceipt'
import SendEmail from '@/modules/company/components/SendEmail'
import StatusToUnpaid from '@/modules/company/components/StatusToUnpaid'

export default {
  components: {
    InfiniteLoading,
    Confirm,
    GenerateReceiptFromOffer,
    PayReceipt,
    SendEmail,
    StatusToUnpaid
  },
  mixins: [flow, applicationSettings],
  inject: [
    'showMsgBox',
    'showLoader',
    'hideLoader',
    'openPdfPrint',
    'closePdfPrint'
  ],
  data: function () {
    return {
      receipts: [],
      unfiskReceipts: [],
      location: {},
      locations: [],
      statusFilter: { name: 'Neplaćeni', value: 'unpayed' },
      filterChoosen: '',
      loadingReceipts: true,
      infiniteCompleted: false,
      lastVisible: null,
      isExecuted: false,
      listeners: [],
      increment: 1,
      selected: {},
      reciptOffer: {},
      multiplier: 0,
      show: false,
      fiscalizing: false,
      aduroId: undefined
    }
  },
  computed: {
    reciptOfferChoices () {
      return [
        { name: this.$tc('$vuetify.receipt', 2), value: 'receipts' },
        { name: this.$tc('$vuetify.offer', 2), value: 'offers' }
      ]
    },
    now () {
      return parseInt((`${new Date().getTime()}` / 1000).toFixed(0))
    },
    locs: {
      get: function () {
        return this.locations
      },
      set: function (nv) {
        this.locations = [
          { name: this.$t('$vuetify.all'), id: '', location_id: '' }
        ].concat(nv)
      }
    },
    filters () {
      if (this.reciptOffer === 'receipts') {
        return [
          { name: 'Plaćeni', value: 'payed' },
          { name: 'Neplaćeni', value: 'unpayed' },
          { name: 'Svi', value: 'all' }
        ]
      } else {
        return [
          { name: 'Svi', value: 'all' }
        ]
      }
    }
  },
  watch: {
    location: {
      deep: true,
      handler (nv, ov) {
        if (nv !== ov) {
          // paginate more columns
          this.lastVisible = {}
          this.receipts = []
          this.unfiskReceipts = []
          this.$refs.InfiniteLoading.stateChanger.reset()
        }
      }
    },
    statusFilter: {
      deep: true,
      handler (nv, ov) {
        if (nv !== ov) {
          this.lastVisible = {}
          this.receipts = []
          this.unfiskReceipts = []
          this.infiniteCompleted = false
          this.$refs.InfiniteLoading.stateChanger.reset()
        }
      }
    }
  },
  mounted () {
    this.getMultiplier()
    this.locations = [
      { name: this.$t('$vuetify.all'), id: '', location_id: '' }
    ]
    this.location = this.locations[0]

    this.reciptOffer = this.reciptOfferChoices[0].value

    const docRef = df.doc(
      `user_locations/${auth.currentUser.uid}.${state.getCurrentCompany().id}`
    )
    const listener = docRef.onSnapshot(doc => {
      if (
        doc &&
        doc.data() &&
        doc.data().locations &&
        Object.keys(doc.data().locations).length > 0
      ) {
        this.locs = Object.keys(doc.data().locations).map(key => {
          return doc.data().locations[key]
        })
      }
    })
    this.listeners.push(listener)
  },
  methods: {
    billingStatusClass (receipt) {
      const status = receipt.payment?.current_status
      if (status === 'PAID') return 'status-ok-exp'
      if (status === 'REVERSED') return 'status-reversed'
      if (receipt.payment_due && parseInt(receipt.payment_due) < this.now) return 'status-warn-after-exp'
      return 'status-warn-exp'
    },
    formatQty (amt) {
      return (amt / this.multiplier).toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDateOnly (timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear()
      return `${day}.${month}.${year}.`
    },
    showPaymentDueDays (paymentDue) {
      if (!paymentDue) return 'Nema datuma'
      const dueDate = new Date(paymentDue * 1000)
      const today = new Date()
      dueDate.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)
      const diffTime = dueDate - today
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24))
      if (diffDays > 0) {
        return `${diffDays} ${diffDays === 1 ? 'dan' : 'dana'}`
      } else if (diffDays < 0) {
        return `Kasni ${Math.abs(diffDays)} ${Math.abs(diffDays) === 1 ? 'dan' : 'dana'}`
      }
      return 'Danas dospijeva'
    },
    issueBefore (issueDateTime) {
      const tmp = new Date(
        toISOLocal(
          new Date(issueDateTime * 1000)
        )
      )

      tmp.setDate(tmp.getDate() + 2)

      const before = parseInt(
        (tmp.getTime() / 1000).toFixed(0)
      )

      return before
    },
    navigateToFlow () {
      this.$router.push({
        name: 'Home',
        params: {
        }
      })
    },
    fiscalizeAll () {
      const that = this
      try {
        this.selected = {}
        const reqId = uuidv4()

        const payload = {
          action: {
            operation: 'update',
            entity: 'fiscalize_bulk',
            resource_id: '',
            params: {
              company_id: state.getCurrentCompany().id
            }
          }
        }

        that.showLoader()
        that.$refs.confirmFiscalize.close()
        df.doc(`request/${reqId}`)
          .set({
            user_id: `${auth.currentUser.uid}`,
            device_id: 'web',
            created: `${new Date().getTime()}`,
            type: 'receipt',
            payload: btoa(
              unescape(encodeURIComponent(JSON.stringify(payload)))
            )
          })
          .then(function () {
          })
          .catch(function (err) {
            that.hideLoader()
            that.lastVisible = {}
            that.receipts = []
            that.unfiskReceipts = []
            that.$refs.InfiniteLoading.stateChanger.reset()
            that.$refs.confirmFiscalize.close()
            that.showMsgBox({
              text: err && err !== '' ? err : 'An error has occurred',
              actions: ['cancel'],
              cancelBtnText: 'OK',
              color: 'error'
            })
          })

        const docRef = df.doc(`response/${reqId}`)
        const unsubscribe = docRef.onSnapshot(function (doc) {
          if (that.$refs && doc.data()) {
            unsubscribe()
            if (!doc.data().header || !doc.data().header.code || doc.data().header.code !== 200) {
              that.showMsgBox({
                text: doc.data().header.error ? doc.data().header.error : 'Nije uspjela fiskalizacija računa!',
                actions: ['cancel'],
                cancelBtnText: that.$t('$vuetify.close'),
                color: 'error'
              })
              that.hideLoader()
              that.lastVisible = {}
              that.receipts = []
              that.unfiskReceipts = []
              that.$refs.InfiniteLoading.stateChanger.reset()
              that.$refs.confirmFiscalize.close()
              return
            }

            that.hideLoader()
            that.lastVisible = {}
            that.receipts = []
            that.unfiskReceipts = []
            that.$refs.InfiniteLoading.stateChanger.reset()
            that.$refs.confirmFiscalize.close()
          }
        })
      } catch (err) {
        that.hideLoader()

        that.lastVisible = {}
        that.receipts = []
        that.unfiskReceipts = []
        that.$refs.InfiniteLoading.stateChanger.reset()
        that.$refs.confirmFiscalize.close()
        that.showMsgBox({
          text: err || 'An error has occurred',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    },
    getMultiplier () {
      const listener = df.doc('project_constants/config').onSnapshot(doc => {
        this.config = doc.data()
        this.multiplier = doc.data().item_amount.decimal_multiplier
      })
      this.listeners.push(listener)
    },
    modifyItems (change) {
      if (change.type === 'added') {
        if (change.doc.data()) {
          const _document = clone(change.doc.data())
          switch (_document.type.flag) {
            case 'R':
            case 'R1':
            case 'R2':
            case 'BO':
              _document.receiptOptions = [
                { title: 'Preuzmi račun', action: this.printReceipt }
              ]
              break
            case 'STORNO':
              _document.receiptOptions = [
                { title: 'Preuzmi račun', action: this.printReceipt }
              ]
              break
            case 'P':
              _document.receiptOptions = [
                { title: 'Preuzmi ponudu', action: this.printReceipt }
              ]
          }

          let rcptSeparator = '-'
          if (_document.company_details.rcpt_separator !== undefined && _document.company_details.rcpt_separator !== '') {
            rcptSeparator = _document.company_details.rcpt_separator
          }

          if (_document.designation) {
            _document.receiptNumber = `${_document.designation.number}${rcptSeparator}${_document.designation.location_id}${rcptSeparator}${_document.designation.register_number}`
          }
          if (_document.issue_date_time) {
            _document.time = _document.issue_date_time.seconds
          }
          if (_document?.type?.flag !== 'BO' && _document?.type?.payer_name) {
            _document.customer = _document.type.payer_name
          } else {
            _document.customer = this.$t('$vuetify.receipts.retailCustomer')
          }

          _document.show = false

          if (
            this.receipts.length > 0 &&
            change.doc.data().issue_date_time >
              this.receipts[this.receipts.length - 1].issue_date_time
          ) {
            this.receipts.unshift({
              ..._document,
              ...{ [`hover-${change.doc.data().id}`]: false }
            })
          } else {
            this.receipts.push({
              ..._document,
              ...{ [`hover-${change.doc.data().id}`]: false }
            })
          }
        }
      }
      if (change.type === 'modified') {
        // TODO: duplicated code, make 1 fun
        this.receipts = this.receipts.map(item => {
          let ret = item
          if (item.id === change.doc.data().id) {
            const _document = clone(change.doc.data())
            switch (_document.type.flag) {
              case 'R':
              case 'R1':
              case 'R2':
              case 'BO':
                _document.receiptOptions = [
                  { title: 'Preuzmi račun', action: this.printReceipt }
                ]
                break
              case 'STORNO':
                _document.receiptOptions = [
                  { title: 'Preuzmi račun', action: this.printReceipt }
                ]
                break
              case 'P':
                _document.receiptOptions = [
                  { title: 'Preuzmi ponudu', action: this.printReceipt }
                ]
            }
            if (_document.designation) {
              _document.receiptNumber = `${_document.designation.number}-${_document.designation.location_id}-${_document.designation.register_number}`
            }
            if (_document.issue_date_time) {
              _document.time = _document.issue_date_time.seconds
            }
            if (_document?.type?.flag !== 'BO' && _document?.type?.payer_name) {
              _document.customer = _document.type.payer_name
            } else {
              _document.customer = this.$t('$vuetify.receipts.retailCustomer')
            }
            ret = _document
          }
          return { ...ret, ...{ [`hover-${change.doc.data().id}`]: false } }
        })
      }

      this.receipts = this.receipts.filter((document, index, self) =>
        index === self.findIndex((t) => (
          t.id === document.id
        ))
      )
    },
    async infiniteHandler ($state) {
      const aduroId = await df.doc('project_constants/aikasa_config/').get()
      this.aduroId = aduroId.data().aduro_company_id
      const currentCompanyOib = state.getCurrentCompany().oib

      const that = this
      const where = null
      const whereFilter = that.statusFilter ? that.statusFilter.value : ''
      let query = df
        .collection(
          `${this.reciptOffer}/${this.aduroId}/${
            this.reciptOffer
          }`
        )
        .where('type.payer_oib', '==', currentCompanyOib)
        .limit(10)

      if (that.lastVisible && that.lastVisible.id) {
        query = query.startAfter(that.lastVisible)
      }

      if (where) {
        query = query.where('designation.location_id', '==', where)
      }

      if (whereFilter === 'unfisc') {
        query = query.where('fiscalize', '==', true).where('jir', '==', '')
      }

      if (whereFilter === 'payed') {
        query = query.where('payment.current_status', '==', 'PAID')
      }

      if (whereFilter === 'unpayed') {
        query = query.where('payment.current_status', 'not-in', ['PAID', 'REVERSED'])
      }

      const listener = query.onSnapshot(
        doc => {
          doc.docChanges().forEach(change => {
            that.modifyItems(change)
          })
          that.receipts = [...that.receipts].sort((a, b) => {
            const toMs = (t) => t?.toMillis?.() ?? (typeof t === 'number' ? t : 0)
            return toMs(b.issue_date_time) - toMs(a.issue_date_time)
          })
          if (doc && !doc.empty) {
            if (doc.docs[doc.docs.length - 1]) {
              that.lastVisible = doc.docs[doc.docs.length - 1]
            }
            that.infiniteCompleted = true
            $state.loaded()
          } else {
            that.infiniteCompleted = true
            $state.complete()
          }
        }
      )

      this.listeners.push(listener)
    },
    getReceiptsOrOffers () {
      this.lastVisible = null
      this.receipts = []
      this.unfiskReceipts = []
      this.detachListeners()

      if (this.$refs.InfiniteLoading) {
        this.$refs.InfiniteLoading.stateChanger.reset()
      }
    },
    async printReceipt (receipt) {
      try {
        this.showLoader()
        const receipttemplate = functions.httpsCallable('receiptTemplate')
        const result = await receipttemplate({
          template: 'racun03',
          companyId: receipt.company_details?.id || this.aduroId,
          documentId: receipt.id,
          documentSubType: 'racun',
          documentType: 'racun',
          rawJson: ''
        })
        if (result.data?.document) {
          this.openPdfPrint(result.data.document)
        } else {
          this.showMsgBox({
            text: this.$t('$vuetify.errors.nullReceipt'),
            actions: ['cancel'],
            cancelBtnText: this.$t('$vuetify.close'),
            color: 'error'
          })
        }
      } catch (err) {
        this.showMsgBox({
          text: err?.message || 'An error has occurred',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      } finally {
        this.hideLoader()
      }
    },
    cancelReceipt () {
      const that = this
      try {
        const receipt = Object.assign({}, this.selected)
        this.selected = {}
        const reqId = uuidv4()

        let receiptNumber = receipt.designation ? receipt.designation.number : 0
        df.doc(`cash_registers/${receipt.designation.register_id}`)
          .get()
          .then(reg => {
            if (reg && reg.exists) {
              if (reg.data()) {
                if (reg.data().status !== 'OK') {
                  this.$refs.confirmStorno.close()
                  that.showMsgBox({
                    text: that.$t('$vuetify.errors.unknownRegister'),
                    actions: ['cancel'],
                    cancelBtnText: that.$t('$vuetify.close'),
                    color: 'error'
                  })
                  return
                }
                if (this.reciptOffer === 'receipts') {
                  receiptNumber = reg.data().bill_sequence || 0
                } else if (this.reciptOffer === 'offers') {
                  receiptNumber = reg.data().offer_sequence || 0
                }
                const payload = {
                  action: {
                    operation: 'set',
                    entity: 'storno',
                    params: {
                      company_id: state.getCurrentCompany().id,
                      location_id: receipt.location_details.id,
                      receipt_id: receipt.id,
                      register_id: receipt.designation.register_id,
                      issue_date_time: parseInt(
                        (new Date().getTime() / 1000).toFixed(0)
                      ),
                      number: receiptNumber + 1
                      // seller_id: auth.currentUser.uid,
                    }
                  }
                }

                that.showLoader()
                that.$refs.confirmStorno.close()
                df.doc(`request/${reqId}`)
                  .set({
                    user_id: `${auth.currentUser.uid}`,
                    device_id: 'web',
                    created: `${new Date().getTime()}`,
                    type: 'receipt',
                    payload: btoa(
                      unescape(encodeURIComponent(JSON.stringify(payload)))
                    )
                  })
                  .then(function () {
                    setTimeout(() => {
                      that.hideLoader()
                      that.lastVisible = {}
                      that.receipts = []
                      that.unfiskReceipts = []
                      that.$refs.InfiniteLoading.stateChanger.reset()
                    }, 5500)
                  })
                  .catch(function (err) {
                    that.hideLoader()
                    that.showMsgBox({
                      text: err && err !== '' ? err : 'An error has occurred',
                      actions: ['cancel'],
                      cancelBtnText: 'OK',
                      color: 'error'
                    })
                  })
              }
            } else {
              that.showMsgBox({
                text: that.$t('$vuetify.errors.unknownRegister'),
                actions: ['cancel'],
                cancelBtnText: that.$t('$vuetify.close'),
                color: 'error'
              })
              that.hideLoader()
              this.$refs.confirmStorno.close()
            }
          })
      } catch (err) {
        this.$refs.confirmStorno.close()
        that.hideLoader()
        that.showMsgBox({
          text: err || 'An error has occurred',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    },
    generateReceipt (receipt) {
      this.$refs.receiptFromOffer.open(receipt, this.multiplier)
    },
    payReceipt (receipt) {
      this.$refs.payReceipt.open(receipt)
    },
    onReceiptEdit (receipt) {
      this.receipts = this.receipts.map(r => {
        if (r.id === receipt.id) {
          r.payment.current_status = 'PROCESSING'
        }
        return r
      })
    },
    statusToUnpaid (receipt) {
      this.$refs.statusToUnpaid.open(receipt)
    },
    fiscalizeReceipt () {
      const that = this
      try {
        const receipt = Object.assign({}, this.selected)
        this.selected = {}
        const reqId = uuidv4()

        const payload = {
          action: {
            operation: 'update',
            entity: 'fiscalize',
            resource_id: receipt.id,
            params: {
              company_id: state.getCurrentCompany().id
            }
          }
        }

        that.showLoader()
        that.$refs.confirmFiscalize.close()
        df.doc(`request/${reqId}`)
          .set({
            user_id: `${auth.currentUser.uid}`,
            device_id: 'web',
            created: `${new Date().getTime()}`,
            type: 'receipt',
            payload: btoa(
              unescape(encodeURIComponent(JSON.stringify(payload)))
            )
          })
          .then(function () {
          })
          .catch(function (err) {
            that.hideLoader()
            that.lastVisible = {}
            that.receipts = []
            that.unfiskReceipts = []
            that.$refs.InfiniteLoading.stateChanger.reset()
            that.$refs.confirmFiscalize.close()
            that.showMsgBox({
              text: err && err !== '' ? err : 'An error has occurred',
              actions: ['cancel'],
              cancelBtnText: 'OK',
              color: 'error'
            })
          })

        const docRef = df.doc(`response/${reqId}`)
        const unsubscribe = docRef.onSnapshot(function (doc) {
          if (that.$refs && doc.data()) {
            unsubscribe()
            if (!doc.data().header || !doc.data().header.code || doc.data().header.code !== 200) {
              that.showMsgBox({
                text: doc.data().header.error ? doc.data().header.error : 'Nije uspjela fiskalizacija računa!',
                actions: ['cancel'],
                cancelBtnText: that.$t('$vuetify.close'),
                color: 'error'
              })
              that.hideLoader()
              that.lastVisible = {}
              that.receipts = []
              that.unfiskReceipts = []
              that.$refs.InfiniteLoading.stateChanger.reset()
              that.$refs.confirmFiscalize.close()
              return
            }

            that.hideLoader()
            that.lastVisible = {}
            that.receipts = []
            that.unfiskReceipts = []
            that.$refs.InfiniteLoading.stateChanger.reset()
            that.$refs.confirmFiscalize.close()
          }
        })
      } catch (err) {
        that.hideLoader()

        that.lastVisible = {}
        that.receipts = []
        that.unfiskReceipts = []
        that.$refs.InfiniteLoading.stateChanger.reset()
        that.$refs.confirmFiscalize.close()
        that.showMsgBox({
          text: err || 'An error has occurred',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    }
  }
}
</script>
<style scoped>
.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 8;
  background: #ffffff;
  padding: 14px 16px 16px;
  margin-bottom: 0;
  border-bottom: 1px solid #EEF2F5;
  border-left: 3px solid #00BCD4;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.filter-select.v-text-field--outlined >>> fieldset {
  border-color: #B0BEC5 !important;
  border-width: 1.5px !important;
  border-radius: 8px !important;
}
.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: #90A4AE !important;
}
.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #00838F !important;
  border-width: 2px !important;
}
.filter-select { min-width: 0 !important; }
.filter-select >>> .v-input__slot {
  background: #ffffff !important;
  min-height: 38px !important;
  max-height: 38px !important;
  padding: 0 8px !important;
  border-radius: 8px !important;
}
.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #263238 !important;
}
.filter-select >>> label {
  font-size: 11px !important;
  font-weight: 500 !important;
  color: #37474F !important;
}

.receipts-rows-body {
  background: #F5F7FA;
  padding: 6px 0 2px;
}
.receipts-rows-body .v-expansion-panels {
  background: transparent !important;
}
.receipts-rows-body .v-expansion-panel:first-child {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}

.v-expansion-panel {
  border: 1px solid #EEF2F5 !important;
  border-left: 3px solid #E0E0E0 !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  box-shadow: none !important;
  overflow: hidden !important;
  transition: border-left-color 0.15s ease, border-color 0.15s ease, background 0.15s ease !important;
  margin-bottom: 4px !important;
}
.v-expansion-panel::before,
.v-expansion-panel::after {
  box-shadow: none !important;
  display: none !important;
}
.v-expansion-panel--active + .v-expansion-panel,
.v-expansion-panel--next-active {
  margin-top: 0 !important;
}
.v-expansion-panel:not(.v-expansion-panel--active):hover {
  border-color: #00BCD4 !important;
  background: #F8FAFB !important;
}
.v-expansion-panel--active {
  border-left-color: #00BCD4 !important;
  border-color: #B2EBF2 !important;
  background: #F7FBFC !important;
}
.v-expansion-panel >>> .v-expansion-panel-header {
  padding-left: 16px !important;
  min-height: 60px !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

.status-ok-exp         { border-left-color: #4CAF50 !important; }
.status-warn-exp       { border-left-color: #FF9800 !important; }
.status-warn-after-exp { border-left-color: #F57C00 !important; }
.status-reversed       { border-left-color: #6154A4 !important; }

.status-ok-exp,
.status-warn-exp,
.status-warn-after-exp,
.status-reversed {
  background: #ffffff !important;
}

.receipt-status-value {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  font-size: 12px;
}
.receipt-status-value::after {
  content: '';
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.status-ok-exp         .receipt-status-value::after { background: #4CAF50; }
.status-warn-exp       .receipt-status-value::after { background: #FF9800; }
.status-warn-after-exp .receipt-status-value::after { background: #F57C00; }
.status-reversed       .receipt-status-value::after { background: #6154A4; }

.receipt-amount-value  { font-weight: 700 !important; }
.receipt-number-value  { font-weight: 700 !important; }

.v-expansion-panel-header .caption.grey--text {
  font-size: 10px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  opacity: 0.6;
  margin-bottom: 2px;
}

.pdv-breakdown-table {
  border-collapse: collapse;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  font-size: 12px;
}

.pdv-breakdown-table th,
.pdv-breakdown-table td {
  padding: 5px 12px;
  text-align: right;
  white-space: nowrap;
  color: #004D58;
}

.pdv-breakdown-table th {
  font-size: 11px;
  font-weight: 600;
  color: #004D58;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(0, 77, 88, 0.12);
}

.pdv-breakdown-table th:first-child,
.pdv-breakdown-table td:first-child {
  text-align: left;
}

.pdv-breakdown-table td.pdv-label {
  font-weight: 600;
  color: #004D58;
}

.pdv-breakdown-table tbody tr {
  background: #ffffff !important;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
}

.empty-state-title {
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
}

.empty-state-subtitle {
  font-size: 14px;
  color: #718096;
  max-width: 340px;
  line-height: 1.6;
}

.empty-state-btn {
  color: #3989C9 !important;
  border-color: rgba(57, 137, 201, 0.35) !important;
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  letter-spacing: 0.2px !important;
  border-radius: 8px !important;
}

.download-btn {
  transition: all 0.2s ease !important;
}

.download-btn:hover {
  box-shadow: 0 0 8px rgba(0, 188, 212, 0.3) !important;
}

.download-icon {
  transition: opacity 0.2s ease !important;
}

.download-btn:hover .download-icon {
  opacity: 0.8 !important;
}

.pdf-icon-stack {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.download-badge-icon {
  position: absolute !important;
  bottom: -4px !important;
  right: -6px !important;
  font-size: 14px !important;
  color: #00BCD4 !important;
  background: white;
  border-radius: 50%;
}

.download-btn:hover .download-badge-icon {
  color: #00acc1 !important;
}
</style>
